import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
interface SplashScreenProps {
  onComplete: () => void;
}
export function SplashScreen({
  onComplete
}: SplashScreenProps) {
  const [phase, setPhase] = useState<'intro' | 'reveal' | 'exit'>('intro');
  useEffect(() => {
    const t1 = setTimeout(() => setPhase('reveal'), 800);
    const t2 = setTimeout(() => setPhase('exit'), 2600);
    const t3 = setTimeout(() => onComplete(), 3200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [onComplete]);
  return <motion.div initial={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.4
  }} className="w-full min-h-screen flex flex-col items-center justify-center relative overflow-hidden" style={{
    backgroundColor: '#0F0A06'
  }} data-id="element-953">
      {/* Animated background blobs */}
      <motion.div className="absolute rounded-full" style={{
      width: 500,
      height: 500,
      left: '-20%',
      top: '-15%',
      background: 'radial-gradient(circle, #661806 0%, transparent 65%)',
      filter: 'blur(80px)'
    }} animate={{
      x: [0, 60, -20, 40, 0],
      y: [0, 40, 80, 20, 0],
      scale: [0.8, 1.2, 0.9, 1.1, 0.8],
      opacity: [0.4, 0.7, 0.5, 0.8, 0.4]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-954" />

      <motion.div className="absolute rounded-full" style={{
      width: 400,
      height: 400,
      right: '-15%',
      bottom: '-10%',
      background: 'radial-gradient(circle, #F37024 0%, transparent 65%)',
      filter: 'blur(80px)'
    }} animate={{
      x: [0, -50, 30, -40, 0],
      y: [0, -30, 50, -20, 0],
      scale: [0.9, 1.15, 0.85, 1.1, 0.9],
      opacity: [0.3, 0.6, 0.35, 0.55, 0.3]
    }} transition={{
      duration: 10,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 0.5
    }} data-id="element-955" />

      <motion.div className="absolute rounded-full" style={{
      width: 300,
      height: 300,
      left: '40%',
      top: '30%',
      background: 'radial-gradient(circle, #E8943A 0%, transparent 65%)',
      filter: 'blur(70px)'
    }} animate={{
      x: [0, 30, -20, 25, 0],
      y: [0, -20, 15, -25, 0],
      scale: [1, 1.3, 0.7, 1.2, 1],
      opacity: [0.15, 0.35, 0.1, 0.3, 0.15]
    }} transition={{
      duration: 7,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} data-id="element-956" />

      {/* Rotating conic ring */}
      <motion.div className="absolute" style={{
      width: 600,
      height: 600,
      left: '50%',
      top: '50%',
      marginLeft: -300,
      marginTop: -300,
      borderRadius: '50%',
      background: 'conic-gradient(from 0deg, transparent, #661806, transparent, #F37024, transparent, #E8943A, transparent)',
      opacity: 0.06,
      filter: 'blur(6px)'
    }} animate={{
      rotate: [0, 360]
    }} transition={{
      duration: 20,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-957" />

      {/* Dot grid texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: 'radial-gradient(circle, #FFF7F3 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} data-id="element-958" />

      {/* Center content */}
      <div className="relative z-10 flex flex-col items-center" data-id="element-959">
        {/* Pulsing ring behind logo */}
        <div className="relative flex items-center justify-center" data-id="element-960">
          <motion.div className="absolute rounded-full" style={{
          width: 180,
          height: 180,
          border: '1px solid rgba(243, 112, 36, 0.2)'
        }} initial={{
          scale: 0.6,
          opacity: 0
        }} animate={phase !== 'intro' ? {
          scale: [0.8, 1.4, 0.8],
          opacity: [0.4, 0, 0.4]
        } : {
          scale: 0.6,
          opacity: 0
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-961" />
          <motion.div className="absolute rounded-full" style={{
          width: 140,
          height: 140,
          border: '1px solid rgba(243, 112, 36, 0.15)'
        }} initial={{
          scale: 0.6,
          opacity: 0
        }} animate={phase !== 'intro' ? {
          scale: [0.9, 1.3, 0.9],
          opacity: [0.3, 0, 0.3]
        } : {
          scale: 0.6,
          opacity: 0
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.4
        }} data-id="element-962" />

          {/* Glow behind logo */}
          <motion.div className="absolute rounded-full" style={{
          width: 120,
          height: 120,
          background: 'radial-gradient(circle, rgba(243, 112, 36, 0.3) 0%, transparent 70%)',
          filter: 'blur(20px)'
        }} initial={{
          scale: 0,
          opacity: 0
        }} animate={phase !== 'intro' ? {
          scale: 1,
          opacity: 1
        } : {
          scale: 0,
          opacity: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-963" />

          {/* Logo */}
          <motion.img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="digiBOP Logo" className="w-56 h-auto relative z-10" initial={{
          opacity: 0,
          scale: 0.7,
          y: 10
        }} animate={phase === 'exit' ? {
          opacity: 0,
          scale: 1.1,
          y: -20
        } : phase === 'reveal' ? {
          opacity: 1,
          scale: 1,
          y: 0
        } : {
          opacity: 0,
          scale: 0.7,
          y: 10
        }} transition={{
          duration: phase === 'exit' ? 0.5 : 0.7,
          ease: phase === 'exit' ? 'easeIn' : [0.16, 1, 0.3, 1]
        }} data-id="element-964" />
        </div>

        {/* Tagline */}
        <motion.p className="text-[14px] font-medium tracking-[0.2em] uppercase mt-6" style={{
        color: 'rgba(255, 247, 243, 0.5)'
      }} initial={{
        opacity: 0,
        y: 12
      }} animate={phase === 'exit' ? {
        opacity: 0,
        y: -8
      } : phase === 'reveal' ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} transition={{
        duration: phase === 'exit' ? 0.3 : 0.6,
        delay: phase === 'reveal' ? 0.3 : 0,
        ease: 'easeOut'
      }} data-id="element-965">
          Smart Banking for Pakistan
        </motion.p>

        {/* Animated line loader */}
        <motion.div className="mt-10 h-[2px] rounded-full overflow-hidden" style={{
        width: 120,
        background: 'rgba(255, 247, 243, 0.08)'
      }} initial={{
        opacity: 0
      }} animate={phase === 'exit' ? {
        opacity: 0
      } : phase === 'reveal' ? {
        opacity: 1
      } : {
        opacity: 0
      }} transition={{
        duration: 0.4,
        delay: phase === 'reveal' ? 0.5 : 0
      }} data-id="element-966">
          <motion.div className="h-full rounded-full" style={{
          background: 'linear-gradient(90deg, #661806, #F37024, #E8943A)'
        }} initial={{
          width: '0%'
        }} animate={phase === 'reveal' ? {
          width: '100%'
        } : {
          width: '0%'
        }} transition={{
          duration: 1.8,
          delay: 0.6,
          ease: [0.4, 0, 0.2, 1]
        }} data-id="element-967" />
        </motion.div>
      </div>

      {/* Bottom branding */}
      <motion.div className="absolute bottom-10 flex flex-col items-center gap-2" initial={{
      opacity: 0
    }} animate={phase === 'exit' ? {
      opacity: 0
    } : phase === 'reveal' ? {
      opacity: 1
    } : {
      opacity: 0
    }} transition={{
      duration: 0.5,
      delay: phase === 'reveal' ? 0.6 : 0
    }} data-id="element-968">
        <div className="flex items-center gap-2 mb-1" data-id="element-969">
          {[0, 1, 2].map(i => <motion.div key={i} className="w-1 h-1 rounded-full" style={{
          background: '#F37024'
        }} animate={{
          opacity: [0.2, 0.8, 0.2],
          scale: [0.8, 1.2, 0.8]
        }} transition={{
          duration: 1.4,
          repeat: Infinity,
          delay: i * 0.2,
          ease: 'easeInOut'
        }} data-id="element-970" />)}
        </div>
        <p className="text-[11px] font-semibold tracking-widest uppercase" style={{
        color: 'rgba(255, 247, 243, 0.3)'
      }} data-id="element-971">
          Bank of Punjab
        </p>
        <p className="text-[9px] font-medium tracking-wider" style={{
        color: 'rgba(255, 247, 243, 0.15)'
      }} data-id="element-972">
          A Digital Banking Experience
        </p>
      </motion.div>

      {/* Decorative corner accents */}
      <motion.div className="absolute top-0 left-0 w-32 h-32" style={{
      background: 'linear-gradient(135deg, rgba(102, 24, 6, 0.3) 0%, transparent 60%)'
    }} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1,
      delay: 0.2
    }} data-id="element-973" />
      <motion.div className="absolute bottom-0 right-0 w-32 h-32" style={{
      background: 'linear-gradient(315deg, rgba(243, 112, 36, 0.2) 0%, transparent 60%)'
    }} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1,
      delay: 0.4
    }} data-id="element-974" />
    </motion.div>;
}